'use client'
import * as React from 'react'

// Slider-based "see how much you're saving" calculator for shoppable
// video. Mirrors the Moast savings page (moast.io/savings): a single
// monthly-views slider drives a big live savings number underneath.
//
// Math: most shoppable-video tools charge per impression (Tolstoy,
// Videowise, Quinn, Bambuser), and stack per-source / per-layout
// surcharges on top. Idukki charges one rate per impression with every
// layout included. Savings = (incumbent CPM − Idukki's plan-amortised
// per-impression cost) × monthly impressions, with the Idukki plan
// stepping through three tiers as traffic scales.
//
// Mirrors shoppableVideoSavings()/SHOPPABLE_VIDEO_IDUKKI_TIERS in
// nextapp/lib/publicToolsMath.ts (nextapp's white-label copy for Feefo, see
// this repo's CLAUDE.md "Public calculator tools").

const INCUMBENT_CPM = 0.012      // $12 per 1k impressions, mid-market shoppable-video CPM
const IDUKKI_TIERS = [
  { uptoViews: 200_000,    monthlyCost: 99  },
  { uptoViews: 1_000_000,  monthlyCost: 199 },
  { uptoViews: Infinity,   monthlyCost: 299 },
]

function idukkiCostFor(views: number) {
  const tier = IDUKKI_TIERS.find((t) => views <= t.uptoViews) ?? IDUKKI_TIERS[IDUKKI_TIERS.length - 1]
  return tier.monthlyCost
}

function format(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`
  return n.toString()
}

const STOPS = [100, 1_000, 5_000, 10_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 2_500_000, 5_000_000]

export function ShoppableVideoSavings() {
  const [stopIndex, setStopIndex] = React.useState(0)
  const views = STOPS[stopIndex]
  const incumbentMonthly = views * INCUMBENT_CPM
  const idukkiMonthly = idukkiCostFor(views)
  const savings = Math.max(0, incumbentMonthly - idukkiMonthly)

  return (
    <div className="mx-auto max-w-4xl px-4 text-center">
      {/* Views badge */}
      <div className="relative mx-auto inline-flex flex-col items-center">
        <span className="inline-flex rounded-2xl bg-primary/10 px-6 py-2 font-display text-2xl font-bold text-primary sm:text-3xl">
          {format(views)} Views
        </span>
        <svg className="-mt-1 text-primary/10" width="20" height="12" viewBox="0 0 20 12" aria-hidden="true">
          <polygon points="0,0 20,0 10,12" fill="currentColor" />
        </svg>
      </div>

      {/* Slider */}
      <div className="mt-2">
        <input
          type="range"
          min={0}
          max={STOPS.length - 1}
          step={1}
          value={stopIndex}
          onChange={(e) => setStopIndex(Number(e.target.value))}
          aria-label="Monthly shoppable video views"
          className="w-full cursor-pointer accent-primary"
        />
        <div className="mt-2 flex justify-between text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          <span>{format(STOPS[0])}</span>
          <span>{format(STOPS[Math.floor(STOPS.length / 2)])}</span>
          <span>{format(STOPS[STOPS.length - 1])}</span>
        </div>
      </div>

      {/* Live savings */}
      <div className="mt-12 flex flex-wrap items-baseline justify-between gap-4 sm:flex-nowrap">
        <p className="font-display text-xl font-semibold text-primary sm:text-2xl">
          Your estimated monthly savings:
        </p>
        <p className="font-display text-4xl font-bold tabular-nums text-primary sm:text-5xl md:text-6xl">
          ${savings.toLocaleString(undefined, { minimumFractionDigits: savings < 100 ? 2 : 0, maximumFractionDigits: savings < 100 ? 2 : 0 })}
        </p>
      </div>

      {/* Helper detail */}
      <p className="mt-3 text-xs text-muted-foreground">
        vs typical shoppable-video pricing at ${(INCUMBENT_CPM * 1000).toFixed(0)}/1k impressions ·
        Idukki flat ${idukkiMonthly}/month at this volume
      </p>
    </div>
  )
}
